/**
 * Language preference — detection + device-level storage.
 *
 * Deliberately dependency-light: `src/i18n.js` imports this to compute the
 * initial language SYNCHRONOUSLY at init time, so this module must not import
 * i18next, React, or any service (which would create an import cycle or pull
 * heavy code into the i18n bootstrap). It only touches `window`/`navigator`.
 *
 * Precedence for the ACTIVE language (see docs/FUTURE_WORK.md i18n phase 2):
 *   explicit account preference  (applied async by AuthContext after login)
 *   > explicit device choice     (localStorage, here)
 *   > browser detection          (navigator, here)
 *   > fallback 'en'
 * The first item needs an authenticated fetch, so it can't participate in the
 * synchronous init — AuthContext reconciles it once auth resolves.
 */

// Language names are shown as endonyms (each in its own language) and are NOT
// translated — this is the i18n convention for a language picker.
export const SUPPORTED_LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'nl', label: 'Nederlands' },
];

export const DEFAULT_LANGUAGE = 'en';

const STORAGE_KEY = 'lyf_language';
const SUPPORTED_CODES = SUPPORTED_LANGUAGES.map((l) => l.code);

export function isSupportedLanguage(code) {
  return typeof code === 'string' && SUPPORTED_CODES.includes(code);
}

// Map a BCP-47 tag ('nl-BE', 'en-US') to a supported base code, or null.
function normalizeToSupported(tag) {
  if (!tag || typeof tag !== 'string') return null;
  const base = tag.toLowerCase().split('-')[0];
  return isSupportedLanguage(base) ? base : null;
}

export function getStoredLanguage() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return isSupportedLanguage(v) ? v : null;
  } catch (e) {
    return null; // private mode / storage disabled
  }
}

export function setStoredLanguage(code) {
  if (!isSupportedLanguage(code)) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch (e) {
    /* storage unavailable — in-memory i18n state still switches for the session */
  }
}

export function detectBrowserLanguage() {
  try {
    const candidates = [
      ...(Array.isArray(navigator.languages) ? navigator.languages : []),
      navigator.language,
    ];
    for (const c of candidates) {
      const match = normalizeToSupported(c);
      if (match) return match;
    }
  } catch (e) {
    /* no navigator (tests) */
  }
  return null;
}

/**
 * Synchronous resolution used as i18next's initial `lng`. The account-level
 * preference is layered on afterwards by AuthContext (async fetch).
 */
export function resolveInitialLanguage() {
  return getStoredLanguage() || detectBrowserLanguage() || DEFAULT_LANGUAGE;
}
